import type { JSX } from 'react'
import { Navigate } from 'react-router-dom'

import { Temporal } from '@js-temporal/polyfill'

import { useLocalStorage } from '@buffer-mono/popcorn'

import { ALL_CHANNELS_TIMEZONE_STORAGE_KEY } from '~publish/components/AllChannelsTimeZoneSelect'
import {
  DEFAULT_VIEW_MODE,
  LAST_VIEW_MODE_LOCAL_STORAGE_KEY,
} from '~publish/components/Calendar/hooks/useCalendar'
import type { CalendarDateRangeViewMode } from '~publish/components/Calendar/hooks/useCalendarDateRangeViewMode'
import {
  DEFAULT_POST_LIST_VIEW_MODE,
  LAST_POST_LIST_VIEW_MODE_LOCAL_STORAGE_KEY,
} from '~publish/components/PostListOrCalendarViewToggle'
import type { ListOrCalendarViewMode } from '~publish/components/PostListOrCalendarViewToggle'
import {
  useAccount,
  useAccountTimezone,
  useUnlockedChannels,
} from '~publish/contexts/AccountContext'
import { CURRENT_TIME_ZONE } from '~publish/helpers/dateFormatters'
import {
  newCalendarSingleChannel,
  schedule,
  scheduleCalendar,
  channelSchedule,
} from '~publish/legacy/routes'

import { useDefaultLandingPage } from '~publish/hooks/useDefaultLandingPage'
import { useHasAccessToBufferHome } from '~publish/hooks/useHasAccessToBufferHome'
import { useWeekStartsOn } from '~publish/hooks/useWeekStartsOn'

function hasStoredScheduleViewPreference(): boolean {
  try {
    return (
      window.localStorage.getItem(
        LAST_POST_LIST_VIEW_MODE_LOCAL_STORAGE_KEY,
      ) !== null
    )
  } catch {
    return false
  }
}

function getNextWeekDateParam(
  weekStartsOn: 'monday' | 'sunday',
  timezone: string,
): string | null {
  const now = Temporal.Now.zonedDateTimeISO(timezone)
  const isLastDayOfWeek =
    (weekStartsOn === 'monday' && now.dayOfWeek === 7) ||
    (weekStartsOn === 'sunday' && now.dayOfWeek === 6)

  return isLastDayOfWeek
    ? now.add({ days: 1 }).epochMilliseconds.toString()
    : null
}

const ScheduleDefaultPath = (): JSX.Element => {
  const connectedChannelsOnOrganization = useUnlockedChannels()
  const weekStartsOn = useWeekStartsOn()
  const accountTimezone = useAccountTimezone()
  const [listOrCalendarViewMode] = useLocalStorage<ListOrCalendarViewMode>(
    LAST_POST_LIST_VIEW_MODE_LOCAL_STORAGE_KEY,
    DEFAULT_POST_LIST_VIEW_MODE,
  )
  const [calendarDateRangeViewMode] =
    useLocalStorage<CalendarDateRangeViewMode>(
      LAST_VIEW_MODE_LOCAL_STORAGE_KEY,
      DEFAULT_VIEW_MODE,
    )
  const [allChannelsTimezone] = useLocalStorage(
    ALL_CHANNELS_TIMEZONE_STORAGE_KEY,
    CURRENT_TIME_ZONE,
  )
  const connectedChannelCount = connectedChannelsOnOrganization?.length ?? 0
  const calendarTimezone =
    connectedChannelCount === 1 ? accountTimezone : allChannelsTimezone
  const isFirstVisitCandidate =
    !hasStoredScheduleViewPreference() &&
    connectedChannelCount >= 1 &&
    connectedChannelCount <= 3

  const defaultPath = (): string => {
    /**
     * Calculate the default path based on the following rules:
     * - If user has only one connected channel -> Redirect to that channel's schedule/calendar
     * - If user has zero or multiple connected channels -> Redirect to the all-channels schedule/calendar
     * - In either view, load calendar/list view based on local storage
     * - New users without a stored preference start in Calendar/week
     */
    const hasOnlyOneConnectedChannel =
      connectedChannelsOnOrganization?.length === 1
    const shouldDefaultToCalendar = isFirstVisitCandidate
    const shouldRedirectToCalendar =
      listOrCalendarViewMode === 'calendar' || shouldDefaultToCalendar
    const calendarGranularity = shouldDefaultToCalendar
      ? DEFAULT_VIEW_MODE
      : calendarDateRangeViewMode
    const initialDateParam = shouldDefaultToCalendar
      ? getNextWeekDateParam(weekStartsOn, calendarTimezone)
      : null
    const withInitialDate = (path: string): string =>
      initialDateParam === null ? path : `${path}?date=${initialDateParam}`

    if (hasOnlyOneConnectedChannel) {
      const channelId = connectedChannelsOnOrganization?.[0]?.id

      // Redirect to Channel View
      return shouldRedirectToCalendar
        ? withInitialDate(
            newCalendarSingleChannel.buildPathname({
              id: channelId,
              granularity: calendarGranularity,
            }),
          )
        : channelSchedule.buildPathname({ id: channelId })
    }

    // Redirect to All Channels view
    return shouldRedirectToCalendar
      ? withInitialDate(
          scheduleCalendar.buildPathname({
            granularity: calendarGranularity,
          }),
        )
      : schedule.route
  }

  return <Navigate to={defaultPath()} replace />
}

export const DefaultPath = (): JSX.Element => {
  const { loading } = useAccount()
  const { hasAccess: hasAccessToHome } = useHasAccessToBufferHome()
  const landingPage = useDefaultLandingPage()

  // Hold the redirect until account ownership resolves so the landing-page
  // preference cannot briefly send an owner to Schedule.
  if (loading) {
    return <></>
  }

  if (hasAccessToHome && landingPage === 'home') {
    return <Navigate to="/home" replace />
  }

  return <ScheduleDefaultPath />
}
